// This file contains common types, constants, etc. used across the viewport-based and
// element-based context providers.

import { Breakpoint, Breakpoints } from '@mui/material';
import { createStore } from 'zustand';
import { FALLBACK_BREAKPOINT } from './fallbackMatchMediaUtils';

/**
 * Simple constant for checking on the list of breakpoints in some rare cases where we need to do vanilla JS.
 * Unfortunately, MUI requires interacting with hooks to get the theme breakpoints, which would be really annoying in
 * some cases where we just want pure JS (e.g. `isResponsiveProp` type guard, fallbacks for testing in cases where the
 * theme provider might be missing).
 *
 *  Defining a simple mapping here and enforcing the entries via types to help us work around these edge cases.
 */
export const breakpointKeyExists: Record<Breakpoint, boolean> = {
  xs: true,
  sm: true,
  md: true,
  lg: true,
  xl: true,
};

/**
 * Array of breakpoint keys that should be treated as part of a "mobile" experience.
 */
const MOBILE_BREAKPOINTS: Breakpoint[] = ['xs', 'sm'];
export const isMobileBreakpoint = (breakpoint: Breakpoint): boolean => {
  return MOBILE_BREAKPOINTS.includes(breakpoint);
};

/**
 * Mapping of theme breakpoints keys to a boolean that is true when that breakpoint matches (i.e. it is currently
 * active). Only a single breakpoint should match at any given time.
 *
 * @example
 * // Active breakpoint is `xl`.
 * { xs: false, sm: false, md: false, lg: false, xl: true };
 */
export type BreakpointMatches = Record<Breakpoint, boolean>;

/**
 * Common types across all breakpoints stores regardless of the status.
 */
export type BreakpointsStoreBase = {
  /**
   * When true, the breakpoints store is loading. The breakpoints store must be loaded before the breakpoint utility
   * hooks can be used.
   */
  isLoading: boolean;
  /**
   * Sets the current active breakpoint and associated state (e.g. `isMobile`, individual entries for breakpoint
   * matches).
   */
  setActiveBreakpoint: (breakpoint: Breakpoint) => void;
};

/**
 * State of a breakpoints store that cannot immediately be initialized because it must wait for additional information
 * (e.g. ElementBreakpointsProvider must wait to receive a `ref` for the element it will measure).
 */
export type BreakpointsStoreLoading = BreakpointsStoreBase & {
  isLoading: true;
};

/**
 * State of a breakpoints store that has been initialized with breakpoint information.
 */
export type BreakpointsStoreReady = BreakpointsStoreBase &
  BreakpointMatches & {
    /**
     * The current active breakpoint.
     */
    activeBreakpoint: Breakpoint;

    isLoading: false;

    /**
     * True when the active breakpoints are treated as a "mobile" experience.
     * Currently includes the following breakpoints: xs, sm.
     *
     * We include this as a separate value in the store because in simple responsive use cases, we may only care about
     * the binary state of mobile/not-mobile. Using a single value for that avoids unnecessary rerenders that would come
     * from listening to the individual breakpoints that fit into "mobile."
     */
    isMobile: boolean;
  };

/**
 * Zustand store that manages the responsive state for breakpoints.
 *
 * This store includes several different abstractions for the breakpoints to enable us to minimize the number of
 * re-renders in cases where a consumer only cares about a very specific state (e.g. `isMobile` being a separate
 * boolean enables the `useIsMobile` hook to only re-render when that specific state changes, instead of on every
 * breakpoint change).
 */
export type BreakpointsStore = BreakpointsStoreLoading | BreakpointsStoreReady;

/**
 * Type guard for differentiating the types of breakpoint state depending on the status.
 */
export const isBreakpointsStoreLoaded = (
  breakpointsStore: BreakpointsStore
): breakpointsStore is BreakpointsStoreReady => {
  return !breakpointsStore.isLoading;
};

/**
 * Generates updated values for a breakpoints store with a new active breakpoint.
 *
 *  @example
 * // Example of updated state for the store when the active breakpoint is `sm`
 * {
 *    xs: false,
 *    sm: true,
 *    md: false,
 *    lg: false,
 *    xl: false,
 *    activeBreakpoint: 'sm',
 *    isMobile: true,
 * };
 */
export function setActiveBreakpoint(
  breakpoints: Breakpoints,
  activeBreakpoint: Breakpoint
): Omit<BreakpointsStoreReady, 'setActiveBreakpoint'> {
  // Re-keying only on the active breakpoint changing to avoid too many event triggers on transitions
  // (one for the prior breakpoint going "false" and one for the new breakpoint going "true") that lead to
  // too many re-renders and odd behavior.
  const keyedMatches: BreakpointMatches = breakpoints.keys.reduce((result, breakpointKey) => {
    return {
      ...result,
      [breakpointKey]: breakpointKey === activeBreakpoint,
    };
  }, {} as BreakpointMatches);

  return { isLoading: false, ...keyedMatches, activeBreakpoint, isMobile: isMobileBreakpoint(activeBreakpoint) };
}

/**
 * Returns a very simplistic store that will be returned ONLY in test environments when the `ViewportBreakpointsProvider`
 * is missing. This makes it easier for consumers to avoid needing the provider in tests where they do not care about
 * testing responsive behavior.
 *
 * This store is not dynamic and always has the active breakpoint set to `FALLBACK_BREAKPOINT`.
 */
export const getFallbackBreakpointsStore = () => {
  return createStore<BreakpointsStore>()(() => {
    return getFallbackBreakpointsStoreInitState();
  });
};

export const getFallbackBreakpointsStoreInitState = () => {
  const fallbackBreakpoints = Object.keys(breakpointKeyExists).reduce((result, breakpointKey) => {
    return {
      ...result,
      [breakpointKey]: breakpointKey === FALLBACK_BREAKPOINT,
    };
  }, {} as BreakpointMatches);

  return {
    isLoading: false,
    ...fallbackBreakpoints,
    activeBreakpoint: FALLBACK_BREAKPOINT,
    isMobile: isMobileBreakpoint(FALLBACK_BREAKPOINT),
    setActiveBreakpoint: () => {
      // noop
    },
  };
};
